import React, { useEffect, useRef, useState, useCallback } from "react";
import { Check, CheckCheck, Download, Play, Loader2 } from "lucide-react";
import { useChatMessages } from "../../../../../api/Chats/chats";
import { Description } from "@mui/icons-material";
import VoiceMessagePlayer from "../../../ChatLayout/Message/MessageInput/VoiceMessagePlayer";
import CallMessage from "../../../ChatLayout/Message/MessagesList/CallMessage";

export default function Message({
  chatId,
  isGroupChat = false,
  currentUserId,
}) {
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  // ✅ refs + highlight state
  const messageRefs = useRef({});
  const [highlightedId, setHighlightedId] = useState(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useChatMessages(chatId);

  const messages =
    data?.pages
      .flatMap((p) => p.messages)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) || [];

  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      setIsScrolledToBottom(true);
    }
  }, [chatId]);

  useEffect(() => {
    if (
      containerRef.current &&
      prevScrollHeight &&
      isFetchingNextPage === false
    ) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeight;
      containerRef.current.scrollTop = scrollDiff;
    }
  }, [isFetchingNextPage, prevScrollHeight]);

  useEffect(() => {
    if (isScrolledToBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, isScrolledToBottom]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    setIsScrolledToBottom(isAtBottom);

    if (el.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      setPrevScrollHeight(el.scrollHeight);
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const formatTime = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDateLabel = (dateString) => {
    const today = new Date();
    const msgDate = new Date(dateString);

    const isToday = msgDate.toDateString() === today.toDateString();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = msgDate.toDateString() === yesterday.toDateString();

    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";

    return msgDate.toLocaleDateString([], {
      day: "numeric",
      month: "short",
      year:
        today.getFullYear() === msgDate.getFullYear() ? undefined : "numeric",
    });
  };

  const isConsecutiveMessage = (currentMsg, prevMsg) => {
    if (!prevMsg) return false;
    return (
      currentMsg.sender._id === prevMsg.sender._id &&
      new Date(currentMsg.createdAt) - new Date(prevMsg.createdAt) < 300000 // 5 minutes
    );
  };

  const renderReplyMessage = (replyTo) => {
    if (!replyTo) return null;

    const handleReplyClick = () => {
      const target = messageRefs.current[replyTo._id];
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });

        // ✅ Add highlight
        setHighlightedId(replyTo._id);

        // ✅ Remove after 2s
        setTimeout(() => {
          setHighlightedId(null);
        }, 2000);
      }
    };

    // Check for multiple files in reply
    const hasMultipleFiles = replyTo.files && replyTo.files.length > 0;
    const hasSingleFile = replyTo.fileUrl;

    return (
      <div
        onClick={handleReplyClick}
        className="bg-gray-100 border-l-4 border-blue-500 pl-2 pr-2 py-1 mb-2 rounded cursor-pointer hover:bg-gray-200 transition"
      >
        <div className="text-xs font-semibold text-blue-600">
          {replyTo.sender?.username || "Unknown"}
        </div>
        <div
          className="text-xs text-gray-600 truncate max-w-[120px]"
          title={replyTo.content || "Document"}
        >
          {hasMultipleFiles ? (
            <span>📎 {replyTo.files.length} files</span>
          ) : hasSingleFile && replyTo.fileType !== "none" ? (
            <span>
              {replyTo.fileType === "image"
                ? "Photo"
                : replyTo.fileType === "video"
                ? "Video"
                : replyTo.fileType === "audio"
                ? "Voice message"
                : "Document"}
            </span>
          ) : (
            replyTo.content || "Message"
          )}
        </div>
      </div>
    );
  };

  // ✅ NEW: Render a single file
  const renderSingleFile = (file, msgId, url, isMine) => {
    const fileName = file.name || url.split("/").pop();

    if (file.type === "audio") {
      return (
        <div className="mb-2" key={file._id || msgId}>
          <VoiceMessagePlayer audioUrl={url} isMine={isMine} />
        </div>
      );
    }

    if (file.type === "image") {
      return (
        <div className="mb-2" key={file._id || msgId}>
          <img
            src={url}
            alt="image"
            className="max-w-[250px] max-h-[200px] rounded-lg object-cover cursor-pointer"
            onClick={() => window.open(url, "_blank")}
          />
        </div>
      );
    }

    if (file.type === "video") {
      return (
        <div
          className="relative max-w-[250px] max-h-[200px] rounded-lg overflow-hidden mb-2"
          key={file._id || msgId}
        >
          <video
            src={url}
            className="w-full h-full object-cover cursor-pointer"
            controls
            onPlay={() => setPlayingVideoId(msgId)}
            onPause={() => setPlayingVideoId(null)}
          />
          {playingVideoId !== msgId && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black bg-opacity-25">
              <Play className="w-8 h-8 text-white" />
            </div>
          )}
        </div>
      );
    }

    // Document/other file types
    return (
      <div
        className="flex items-center gap-2 p-2 bg-white/10 text-current rounded cursor-pointer hover:bg-white/20 transition-colors mb-2"
        onClick={() => window.open(url, "_blank")}
        title={fileName}
        key={file._id || msgId}
      >
        <Description size={18} />
        <span className="truncate max-w-[150px]">{fileName}</span>
        <a
          href={url}
          download
          className="ml-auto hover:bg-white/20 p-1 rounded"
          onClick={(e) => e.stopPropagation()}
        >
          <Download size={16} />
        </a>
      </div>
    );
  };

  // ✅ UPDATED: Handle both single file and multiple files
  const renderFileMessage = (msg, isMine) => {
    // Handle new format with files array
    if (msg.files && msg.files.length > 0) {
      // Single file
      if (msg.files.length === 1) {
        const file = msg.files[0];
        const url = file.url.startsWith("http")
          ? file.url
          : `${BASE_DOWNLOAD_URL}${file.url}`;
        return renderSingleFile(file, msg._id, url, isMine);
      }

      // Multiple files - render in grid
      const images = msg.files.filter((f) => f.type === "image");
      const videos = msg.files.filter((f) => f.type === "video");
      const audios = msg.files.filter((f) => f.type === "audio");
      const documents = msg.files.filter(
        (f) =>
          f.type === "document" ||
          (f.type !== "image" && f.type !== "video" && f.type !== "audio")
      );

      return (
        <div className="space-y-2">
          {/* Images Grid */}
          {images.length > 0 && (
            <div
              className={`grid gap-2 ${
                images.length === 1
                  ? "grid-cols-1"
                  : images.length === 2
                  ? "grid-cols-2"
                  : images.length === 3
                  ? "grid-cols-3"
                  : "grid-cols-2"
              }`}
            >
              {images.map((file, idx) => {
                const url = file.url.startsWith("http")
                  ? file.url
                  : `${BASE_DOWNLOAD_URL}${file.url}`;
                return renderSingleFile(
                  file,
                  `${msg._id}-img-${idx}`,
                  url,
                  isMine
                );
              })}
            </div>
          )}

          {/* Videos Grid */}
          {videos.length > 0 && (
            <div
              className={`grid gap-2 ${
                videos.length === 1 ? "grid-cols-1" : "grid-cols-2"
              }`}
            >
              {videos.map((file, idx) => {
                const url = file.url.startsWith("http")
                  ? file.url
                  : `${BASE_DOWNLOAD_URL}${file.url}`;
                return renderSingleFile(
                  file,
                  `${msg._id}-vid-${idx}`,
                  url,
                  isMine
                );
              })}
            </div>
          )}

          {/* Audio Files */}
          {audios.length > 0 && (
            <div className="space-y-2">
              {audios.map((file, idx) => {
                const url = file.url.startsWith("http")
                  ? file.url
                  : `${BASE_DOWNLOAD_URL}${file.url}`;
                return renderSingleFile(
                  file,
                  `${msg._id}-aud-${idx}`,
                  url,
                  isMine
                );
              })}
            </div>
          )}

          {/* Documents List */}
          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((file, idx) => {
                const url = file.url.startsWith("http")
                  ? file.url
                  : `${BASE_DOWNLOAD_URL}${file.url}`;
                return renderSingleFile(
                  file,
                  `${msg._id}-doc-${idx}`,
                  url,
                  isMine
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Handle old format with fileUrl and fileType
    if (!msg.fileUrl || msg.fileType === "none") return null;

    const url = msg.fileUrl.startsWith("http")
      ? msg.fileUrl
      : `${BASE_DOWNLOAD_URL}${msg.fileUrl}`;

    const file = {
      _id: msg._id,
      type: msg.fileType,
      url: msg.fileUrl,
      name: url.split("/").pop(),
    };

    return renderSingleFile(file, msg._id, url, isMine);
  };

  if (!chatId)
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Select a chat
      </div>
    );
  if (isLoading)
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading messages...
      </div>
    );
  if (isError)
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        Failed to load messages
      </div>
    );

  return (
    <div
      className="flex flex-col h-full overflow-y-auto p-4 space-y-1 bg-gray-50"
      ref={containerRef}
      onScroll={handleScroll}
    >
      {/* Loading indicator for older messages */}
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading older messages...
          </div>
        </div>
      )}

      {messages.length > 0 ? (
        <>
          {messages.map((msg, index) => {
            const isMe = msg.sender._id === currentUserId;
            const prevMsg = index > 0 ? messages[index - 1] : null;
            const isConsecutive = isConsecutiveMessage(msg, prevMsg);

            const readByOthers =
              msg.readBy?.filter((reader) => reader._id !== msg.sender._id) ||
              [];
            const isRead = readByOthers.length > 0;

            const showDateDivider =
              index === 0 ||
              new Date(msg.createdAt).toDateString() !==
                new Date(prevMsg?.createdAt).toDateString();

            // ✅ NEW: Check if this is a call message
            if (msg.messageType === "call") {
              return (
                <React.Fragment key={msg._id}>
                  {showDateDivider && (
                    <div className="flex justify-center my-4">
                      <span className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded-full">
                        {formatDateLabel(msg.createdAt)}
                      </span>
                    </div>
                  )}
                  <CallMessage message={msg} currentUserId={currentUserId} />
                </React.Fragment>
              );
            }

            // Regular message rendering
            return (
              <React.Fragment key={msg._id}>
                {showDateDivider && (
                  <div className="flex justify-center my-4">
                    <span className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded-full">
                      {formatDateLabel(msg.createdAt)}
                    </span>
                  </div>
                )}

                <div
                  ref={(el) => (messageRefs.current[msg._id] = el)}
                  className={`flex ${isMe ? "justify-end" : "justify-start"} ${
                    isConsecutive ? "mt-1" : "mt-3"
                  } group`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[70%] ${
                      isMe ? "mr-2" : "ml-2"
                    }`}
                  >
                    {isGroupChat && !isMe && !isConsecutive && (
                      <div className="mb-1 ml-1">
                        <span className="text-xs font-medium text-teal-600">
                          {msg.sender?.username}
                        </span>
                      </div>
                    )}

                    <div
                      className={`relative max-w-xl px-3 py-2 rounded-lg shadow-sm ${
                        isMe
                          ? "bg-[#0070f0] text-white rounded-br-none"
                          : "bg-[#F3F4f6] text-black rounded-bl-none"
                      } ${
                        highlightedId === msg._id ? "highlight-animate" : ""
                      }`}
                    >
                      {renderReplyMessage(msg.replyTo)}
                      {renderFileMessage(msg, isMe)}
                      {msg.content && (
                        <div className="text-sm whitespace-pre-wrap leading-relaxed break-words">
                          {msg.content}
                        </div>
                      )}

                      {/* Timestamp - Show on all messages */}
                      <div
                        className={`flex items-center justify-end mt-1 gap-1 text-[10px] ${
                          isMe ? "text-gray-200" : "text-gray-500"
                        }`}
                      >
                        <span>{formatTime(msg.createdAt)}</span>
                        {isMe && (
                          <>
                            {isRead ? (
                              <CheckCheck
                                size={14}
                                className="text-blue-400"
                                strokeWidth={2.5}
                              />
                            ) : (
                              <Check
                                size={14}
                                className="text-gray-300"
                                strokeWidth={2.5}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </>
      ) : (
        <div className="flex items-center justify-center h-full text-gray-500 text-sm">
          <div className="text-center">
            <div className="text-2xl mb-2">💬</div>
            <div>No messages yet</div>
            <div className="text-xs mt-1">Start the conversation!</div>
          </div>
        </div>
      )}
    </div>
  );
}